<script setup>
import { Link, router } from '@inertiajs/vue3'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * RED-390: Aruande kontroll → Kasutus ja kulu (design-spec §7). What the
 * report quality check cost per month: four tiles, every run (20 per page,
 * newest first) and the month split by company. The numbers come from
 * `ReportQcUsage`, the same query `report-qc:costs` prints. Filters and paging
 * are query parameters; changing one partial-reloads only the `usage` prop.
 * The appraiser never sees this page.
 */
const props = defineProps({
  usage: { type: Object, required: true },
})

const { t } = useI18n()

const filters = computed(() => props.usage.filters)
const totals = computed(() => props.usage.totals)

const money = new Intl.NumberFormat('et-EE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })
const usd = value => (value === null || value === undefined ? '—' : `${money.format(value)} USD`)

const monthName = new Intl.DateTimeFormat('et-EE', { month: 'long', year: 'numeric' })
const monthOptions = computed(() =>
  props.usage.months.map(month => {
    const [year, number] = month.split('-').map(Number)
    return { value: month, label: monthName.format(new Date(year, number - 1, 1)) }
  }),
)

const companyOptions = computed(() => [
  { value: null, label: t('messages.admin.report_qc.usage.companyAll') },
  ...props.usage.companies.map(company => ({ value: company.id, label: company.name })),
])

const loading = ref(false)

/** One place builds the query string, so a filter change resets the page. */
const visit = (changes, page = 1) => {
  const next = { ...filters.value, ...changes }
  router.get(
    route('admin.report-qc.index'),
    {
      part: 'kasutus',
      month: next.month,
      company: next.company ?? undefined,
      partial: next.partial ? 1 : undefined,
      page: page > 1 ? page : undefined,
    },
    {
      only: ['usage'],
      preserveState: true,
      preserveScroll: true,
      replace: true,
      onStart: () => (loading.value = true),
      onFinish: () => (loading.value = false),
    },
  )
}

const onPage = event => visit({}, event.page + 1)

// The `pooleli jäänud` tile is the filter switch; with nothing to show it stays put.
const partialClickable = computed(() => filters.value.partial || totals.value.partial > 0)
const togglePartial = () => {
  if (partialClickable.value) {
    visit({ partial: !filters.value.partial })
  }
}

const dateTime = iso =>
  iso ? new Intl.DateTimeFormat('et-EE', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso)) : ''

const duration = seconds => {
  if (seconds === null || seconds === undefined) {
    return '—'
  }
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m > 0
    ? t('messages.admin.report_qc.usage.durationMinSec', { m, s })
    : t('messages.admin.report_qc.usage.durationSec', { s })
}

const questions = row =>
  row.total === null || row.total === undefined ? '—' : `${row.done ?? 0} / ${row.total}`

const stateClass = state =>
  ({
    done: 'text-emerald-700',
    running: 'text-gray-600',
    failed: 'text-red-600',
  })[state] ?? 'text-amber-700'

const emptyText = computed(() =>
  filters.value.partial && totals.value.runs > 0
    ? t('messages.admin.report_qc.usage.emptyPartial')
    : t('messages.admin.report_qc.usage.empty'),
)
</script>

<template>
  <Card data-testid="usage-report">
    <template #title>{{ t('messages.admin.report_qc.nav.kasutus') }}</template>
    <template #subtitle>{{ t('messages.admin.report_qc.usage.hint') }}</template>
    <template #content>
      <div class="mb-4 flex flex-wrap items-center gap-3">
        <Select
          :model-value="filters.month"
          :options="monthOptions"
          option-label="label"
          option-value="value"
          class="w-full md:w-56"
          :aria-label="t('messages.admin.report_qc.usage.monthLabel')"
          data-testid="usage-filter-month"
          @update:model-value="value => visit({ month: value, company: null, partial: false })"
        />
        <Select
          :model-value="filters.company"
          :options="companyOptions"
          option-label="label"
          option-value="value"
          :placeholder="t('messages.admin.report_qc.usage.companyAll')"
          filter
          class="w-full md:w-56"
          :aria-label="t('messages.admin.report_qc.usage.companyAll')"
          data-testid="usage-filter-company"
          @update:model-value="value => visit({ company: value })"
        />
      </div>

      <div class="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <div class="rounded-md border border-gray-200 p-3" data-testid="usage-tile-runs">
          <p class="text-xl font-medium">{{ totals.runs }}</p>
          <p class="text-sm text-gray-600">{{ t('messages.admin.report_qc.usage.tileRuns') }}</p>
        </div>
        <div class="rounded-md border border-gray-200 p-3" data-testid="usage-tile-usd">
          <p class="text-xl font-medium" data-testid="usage-total-usd">{{ usd(totals.usd) }}</p>
          <p class="text-sm text-gray-600">{{ t('messages.admin.report_qc.usage.tileUsd') }}</p>
        </div>
        <div class="rounded-md border border-gray-200 p-3" data-testid="usage-tile-average">
          <p class="text-xl font-medium">{{ usd(totals.average) }}</p>
          <p class="text-sm text-gray-600">{{ t('messages.admin.report_qc.usage.tileAverage') }}</p>
        </div>
        <button
          type="button"
          class="rounded-md border p-3 text-left"
          :class="[
            filters.partial ? 'border-amber-400 bg-amber-50' : 'border-gray-200',
            partialClickable ? 'cursor-pointer hover:border-amber-400' : 'cursor-default',
          ]"
          :aria-pressed="filters.partial"
          :disabled="!partialClickable"
          v-tooltip.top="partialClickable ? t('messages.admin.report_qc.usage.tilePartial_tooltip') : null"
          data-testid="usage-tile-partial"
          @click="togglePartial"
        >
          <p class="text-xl font-medium">{{ totals.partial }}</p>
          <p class="text-sm text-gray-600">{{ t('messages.admin.report_qc.usage.tilePartial') }}</p>
        </button>
      </div>

      <p
        v-if="filters.partial"
        class="mb-3 flex flex-wrap items-center gap-2 text-sm text-gray-700"
        data-testid="usage-partial-on"
      >
        {{ t('messages.admin.report_qc.usage.partialOn') }}
        <Button
          :label="t('messages.admin.report_qc.usage.showAll')"
          link
          size="small"
          class="w-auto! p-0!"
          data-testid="usage-show-all"
          @click="visit({ partial: false })"
        />
      </p>

      <DataTable
        :value="usage.rows"
        lazy
        paginator
        :rows="usage.perPage"
        :total-records="usage.total"
        :first="(usage.page - 1) * usage.perPage"
        :loading="loading"
        data-key="id"
        class="text-sm"
        data-testid="usage-table"
        @page="onPage"
      >
        <template #empty>
          <p class="py-4 text-center text-gray-500" data-testid="usage-empty">{{ emptyText }}</p>
        </template>
        <Column :header="t('messages.admin.report_qc.usage.colAt')">
          <template #body="{ data }">
            <span class="whitespace-nowrap" data-testid="usage-row">{{ dateTime(data.at) }}</span>
          </template>
        </Column>
        <Column :header="t('messages.admin.report_qc.usage.colEvaluation')">
          <template #body="{ data }">
            <span v-if="data.deleted" class="text-gray-400">{{ t('messages.admin.report_qc.usage.evaluationDeleted') }}</span>
            <!-- RED-389: every row that is not deleted opens its check, read-only. -->
            <Link
              v-else
              :href="route('admin.report-qc.check', { reportCheck: data.id, from: 'kasutus' })"
              class="text-primary-700 hover:underline"
              data-testid="usage-open"
            >{{ data.address ?? `#${data.evaluationId}` }}</Link>
          </template>
        </Column>
        <Column :header="t('messages.admin.report_qc.usage.colAppraiser')">
          <template #body="{ data }">
            <p class="text-gray-800">{{ data.appraiser ?? '—' }}</p>
            <p class="text-xs text-gray-500">{{ data.company ?? t('messages.admin.report_qc.usage.companyNone') }}</p>
          </template>
        </Column>
        <Column :header="t('messages.admin.report_qc.usage.colQuestions')">
          <template #body="{ data }">
            <span class="whitespace-nowrap">{{ questions(data) }}</span>
          </template>
        </Column>
        <Column :header="t('messages.admin.report_qc.usage.colDuration')">
          <template #body="{ data }">
            <span class="whitespace-nowrap">{{ duration(data.durationSeconds) }}</span>
          </template>
        </Column>
        <Column :header="t('messages.admin.report_qc.usage.colUsd')">
          <template #body="{ data }">
            <span class="whitespace-nowrap" data-testid="usage-row-usd">{{ usd(data.usd) }}</span>
          </template>
        </Column>
        <Column :header="t('messages.admin.report_qc.usage.colState')">
          <template #body="{ data }">
            <span
              class="whitespace-nowrap font-medium"
              :class="stateClass(data.state)"
              :data-state="data.state"
              data-testid="usage-row-state"
            >{{ t(`messages.admin.report_qc.usage.state.${data.state}`) }}</span>
          </template>
        </Column>
        <Column :header="t('messages.admin.report_qc.usage.colSettings')">
          <template #body="{ data }">
            <span v-if="data.settingsVersion" class="whitespace-nowrap font-mono text-xs text-gray-500">
              {{ t('messages.admin.report_qc.usage.settingsVersion', { n: data.settingsVersion }) }}
            </span>
            <span
              v-else
              v-tooltip.top="t('messages.admin.report_qc.usage.settingsNone')"
              class="text-gray-400"
              tabindex="0"
            >—</span>
          </template>
        </Column>
      </DataTable>

      <div v-if="usage.byCompany.length" class="mt-6" data-testid="usage-by-company">
        <h3 class="mb-2 text-lg font-medium">{{ t('messages.admin.report_qc.usage.byCompanyTitle') }}</h3>
        <DataTable :value="usage.byCompany" class="text-sm">
          <Column :header="t('messages.admin.report_qc.usage.colCompany')">
            <template #body="{ data }">{{ data.company ?? t('messages.admin.report_qc.usage.companyNone') }}</template>
          </Column>
          <Column :header="t('messages.admin.report_qc.usage.colRuns')" field="runs" />
          <Column :header="t('messages.admin.report_qc.usage.colTotal')">
            <template #body="{ data }"><span class="whitespace-nowrap">{{ usd(data.usd) }}</span></template>
          </Column>
          <Column :header="t('messages.admin.report_qc.usage.colAverage')">
            <template #body="{ data }"><span class="whitespace-nowrap">{{ usd(data.average) }}</span></template>
          </Column>
          <ColumnGroup type="footer">
            <Row>
              <Column :footer="t('messages.admin.report_qc.usage.totalRow')" />
              <Column :footer="String(usage.monthTotals.runs)" />
              <Column :footer="usd(usage.monthTotals.usd)" footer-class="whitespace-nowrap" />
              <Column :footer="usd(usage.monthTotals.average)" footer-class="whitespace-nowrap" />
            </Row>
          </ColumnGroup>
        </DataTable>
      </div>
    </template>
  </Card>
</template>
